/*
 * pcx-capture-errors.js — console / JS error hygiene capture module.
 *
 *   Halide-portable (spec replay-v2 R6): ZERO Shopify/Liquid dependencies.
 *   All wiring (where to POST, the page path, the session/visitor ids) is
 *   injected via init(deps) by the adapter (pcx-capture-adapter.js).
 *
 *   Captures four error classes and maps them to error_type:
 *     window.onerror              -> 'onerror'
 *     unhandledrejection          -> 'unhandled'
 *     console.error(...)          -> 'error'
 *     console.warn(...)           -> 'warn'
 *
 *   Client-side dedupe: identical (message-template, source, line) errors are
 *   aggregated into ONE entry with a count, so a tight error loop sends one
 *   row with count=K rather than K rows. The batch is flushed to the
 *   /error-collect sibling endpoint (NOT /collect — hygiene data is
 *   structurally segregated from funnel events).
 *
 *   Field NAMES only / no PII: the message text is the error's own message,
 *   which is visitor-/script-controlled content. It is length-bounded server-
 *   side (reject-not-truncate); this module also bounds it client-side as a
 *   courtesy, but the server is the load-bearing cap.
 *
 *   Integration:
 *     PeterCoxCapture.errors.init({
 *       send: fn(payload),                  // REQUIRED — POSTs {entries:[...], page_path}
 *       maxEntries: 30,                      // OPTIONAL — unique-entry cap before flush
 *       messageMaxChars: 1000                // OPTIONAL — client-side message bound
 *     });
 */
(function () {
  'use strict';

  /* PURE HELPERS BEGIN — extracted + unit-tested in tests/pcx-capture-errors.test.mjs */

  // Normalise a raw error message into a stable dedupe TEMPLATE: collapse
  // runs of digits to '#' and trim, so "Cannot read x of null at line 42" and
  // "... at line 99" collapse to one template. Bounded to messageMaxChars
  // (client-side courtesy; the server applies the load-bearing reject-not-
  // truncate cap). Always returns a string (empty input -> '').
  function normaliseMessage(raw, messageMaxChars) {
    var s = (raw == null) ? '' : String(raw);
    s = s.replace(/\d+/g, '#').replace(/\s+/g, ' ').trim();
    if (typeof messageMaxChars === 'number' && messageMaxChars > 0 && s.length > messageMaxChars) {
      s = s.slice(0, messageMaxChars);
    }
    return s;
  }

  // Map a capture source to the bounded error_type enum. Unknown sources map
  // to null so the caller drops them (never store an out-of-enum type).
  var ERROR_TYPES = { onerror: 'onerror', unhandledrejection: 'unhandled',
                      'console.error': 'error', 'console.warn': 'warn' };
  function classifyError(source) {
    return Object.prototype.hasOwnProperty.call(ERROR_TYPES, source)
      ? ERROR_TYPES[source] : null;
  }

  // Stable dedupe key for an entry: error_type + normalised message + source +
  // line. Two errors with the same key are the same incident.
  function dedupeKey(entry) {
    return [entry.error_type, entry.message, entry.source || '', entry.line == null ? '' : entry.line].join('');
  }

  // Fold a new entry into the running aggregate map. Returns the map. An entry
  // whose key already exists bumps its count instead of adding a row. The map
  // is capped at maxEntries DISTINCT keys — once full, a new distinct key is
  // dropped and overflow is flagged (the caller reads map.__overflow).
  function aggregate(map, entry, maxEntries) {
    var key = dedupeKey(entry);
    if (Object.prototype.hasOwnProperty.call(map, key)) {
      map[key].count += (entry.count || 1);
      return map;
    }
    // count distinct non-meta keys
    var distinct = 0;
    for (var k in map) {
      if (k !== '__overflow' && Object.prototype.hasOwnProperty.call(map, k)) distinct++;
    }
    if (typeof maxEntries === 'number' && distinct >= maxEntries) {
      map.__overflow = true;
      return map;
    }
    map[key] = {
      error_type: entry.error_type,
      message: entry.message,
      source: entry.source || null,
      line: (entry.line == null ? null : entry.line),
      col: (entry.col == null ? null : entry.col),
      count: entry.count || 1
    };
    return map;
  }

  // Turn the aggregate map into the entries array for the payload.
  function mapToEntries(map) {
    var out = [];
    for (var k in map) {
      if (k === '__overflow' || !Object.prototype.hasOwnProperty.call(map, k)) continue;
      out.push(map[k]);
    }
    return out;
  }

  /* PURE HELPERS END */

  function init(deps) {
    deps = deps || {};
    var send = deps.send;
    if (typeof send !== 'function') return false;
    var maxEntries = (typeof deps.maxEntries === 'number') ? deps.maxEntries : 30;
    var messageMaxChars = (typeof deps.messageMaxChars === 'number') ? deps.messageMaxChars : 1000;

    var map = {};

    function record(source, rawMessage, file, line, col) {
      var error_type = classifyError(source);
      if (!error_type) return;
      var message = normaliseMessage(rawMessage, messageMaxChars);
      if (!message) return;
      aggregate(map, {
        error_type: error_type,
        message: message,
        source: file || null,
        line: (typeof line === 'number' ? line : null),
        col: (typeof col === 'number' ? col : null),
        count: 1
      }, maxEntries);
    }

    function flush() {
      var entries = mapToEntries(map);
      if (!entries.length) return;
      var payload = { entries: entries };
      if (map.__overflow) payload.overflow = true;
      if (deps.pagePath) payload.page_path = deps.pagePath;
      map = {};
      send(payload);
    }

    // ── Browser wiring (impure — not in the pure-helpers block) ──────────────
    var w = (typeof window !== 'undefined') ? window : null;
    if (w) {
      w.addEventListener('error', function (ev) {
        record('onerror', ev && ev.message, ev && ev.filename,
               ev && ev.lineno, ev && ev.colno);
      });
      w.addEventListener('unhandledrejection', function (ev) {
        var reason = ev && ev.reason;
        var msg = (reason && reason.message) ? reason.message : reason;
        record('unhandledrejection', msg, null, null, null);
      });
      if (w.console) {
        ['error', 'warn'].forEach(function (level) {
          var orig = w.console[level];
          if (typeof orig !== 'function') return;
          w.console[level] = function () {
            try {
              record('console.' + level, Array.prototype.join.call(arguments, ' '), null, null, null);
            } catch (e) { /* never let capture break the page */ }
            return orig.apply(w.console, arguments);
          };
        });
      }
      // Flush on page hide (covers tab close / navigation) and on a debounced
      // timer so a long-lived page eventually reports.
      w.addEventListener('pagehide', flush);
      w.addEventListener('visibilitychange', function () {
        if (w.document && w.document.visibilityState === 'hidden') flush();
      });
    }

    init._flush = flush;   // exposed for the adapter / tests
    return true;
  }

  window.PeterCoxCapture = window.PeterCoxCapture || {};
  window.PeterCoxCapture.errors = { init: init };
})();
